import { getIntl } from "localization/intl";

export enum ErrorCode {
	/** Authentication error codes */
	AUTH_USER_NOT_FOUND = "auth/user-not-found",
	AUTH_EMAIL_ALREADY_VERIFIED = "auth/email-already-verified",
	AUTH_EMAIL_ALREADY_EXISTS = "auth/email-already-exists",
	AUTH_EMAIL_NOT_PART_OF_ORG = "auth/email-not-part-of-organization",
	AUTH_REGISTRATION_NOT_ALLOWED = "auth/passwordless-registration-not-allowed",
	AUTH_ONLY_SUPER_ADMIN_ALLOWED = "auth/only-super-admin-allowed",
	AUTH_TOS_NOT_ACCEPTED = "auth/tos-not-accepted",
	AUTH_INVALID_OTP = "auth/invalid-otp",
	AUTH_OTP_EXPIRED = "auth/otp-expired",
	AUTH_INVALID_CREDENTIALS = "auth/invalid-credentials",
	AUTH_ROOM_NOT_AVAILABLE = "auth/room-not-available",
	AUTH_ROOM_NOT_EXIST = "auth/room-not-exist",
	AUTH_ROOM_INACCESSIBLE = "auth/room-inaccessible",
	AUTH_DOMAIN_NOT_ALLOWED = "auth/domain-not-allowed",

	/** Room error codes */
	ROOM_NOT_FOUND = "auth/room/not-found",
	ROOM_TEMPLATE_NOT_FOUND = "auth/room-template/not-found",

	ROOM_TEMPLATE_USED_IN_JOURNEY = "room-template/used-in-journey",

	/** File error codes */
	FILE_INVALID_DATA = "file/invalid-file-data",
	FILE_NOT_FOUND = "file/not-found",
	FILE_SIZE_EXCEED = "file/size-exceed",
	FILE_TYPE_NOT_ALLOWED = "file/type-not-allowed",

	/** Organization error codes */
	ORG_NOT_FOUND = "organization/not-found",
	ORG_PLAN_NOT_FOUND = "organization-plan/not-found",
	ORG_LOCATION_NOT_FOUND = "organization-location/not-found",
	ORG_DEPARTMENT_NOT_FOUND = "organization-department/not-found",
	ORG_DEPARTMENT_IN_USE = "organization-department/in-use",
	ORG_LOCATION_IN_USE = "organization-location/in-use",
	ORG_PROFILE_INVALID_CAREER_PAGE_URL = "organization-profile/invalid-career-page-url",
	ORG_PROFILE_INVALID_WEBSITE_URL = "organization-profile/invalid-website-url",
	ORG_PROFILE_INVALID_LINKEDIN_URL = "organization-profile/invalid-linkedIn-url",

	/** Journey error codes */
	JOURNEY_NOT_FOUND = "journey/not-found",
	JOURNEY_IN_DRAFT = "journey/in-draft",
	JOURNEY_IN_USE = "journey/in-use",
	JOURNEY_NOT_TYPE_UPLOAD_FILE = "journey/not-upload-file",
	JOURNEY_ROUTE_NOT_EXIST = "journey/publish-route-not-exist",
	JOURNEY_NO_ACTIVITY_FOUND = "journey/publish-no-activity-found",
	JOURNEY_USER_ALREADY_ASSIGNED = "journey/user-already-assigned",
	JOURNEY_USER_FORBIDDEN_ASSIGN = "journey/user-forbidden-assign",

	/** Stripe error codes */
	STRIPE_NOT_ENABLED = "stripe/not-enabled",
	STRIPE_UNPAID_SUBSCRIPTION = "stripe/unpaid-subscription",
	STRIPE_CANCELLED_AT_PERIOD_END = "stripe/cancelled-at-period-end",
	STRIPE_JOURNEY_BUILDER_CREDIT_LIMIT = "stripe/journey-builder/credit-limit",
	STRIPE_STORY_CREDIT_LIMIT = "stripe/story/credit-limit",
	STRIPE_ASSIGN_JOURNEY_CREDIT_LIMIT = "stripe/assign-journey/credit-limit",

	/** Email error codes */
	EMAIL_SENDER_EMPTY = "email/sender-empty",
	EMAIL_RECEIVER_EMPTY = "email/receiver-empty",
	EMAIL_RATE_LIMIT_HOURLY = "email/rate-limit-hourly",
	EMAIL_RATE_LIMIT_FREQUENCY = "email/rate-limit-frequency",

	/** Expired error codes */
	EXPIRED_RESET_PASSWORD = "expired/reset-password",

	/** Daily mission error codes */
	DAILY_MISSION_NOT_FOUND = "daily-mission/not-found",
	DAILY_MISSION_WRONG_PIN = "daily-mission/wrong-pin",
	DAILY_MISSION_ORDER_ALREADY_EXISTS = "daily-mission/order-already-exists",

	/** User Goal Validation ERROR */
	USER_GOAL_NOT_FOUND = "user-goal/not-found",
	USER_GOAL_IS_COMPLETED = "user-goal/is-completed",

	/** Questionnaire Validation ERROR */
	QUESTIONNAIRE_NOT_FOUND = "questionnaire/not-found",
	QUESTIONNAIRE_ORDER_ALREADY_EXISTS = "questionnaire/order-already-exists",
	QUESTION_NOT_FOUND = "question/not-found",

	/** Task Validation ERROR */
	TASK_NOT_FOUND = "task/not-found",
	USER_TASK_NOT_FOUND = "user-task/not-found",

	/** User Questionnaire Validation ERROR */
	USER_QUESTIONNAIRE_NOT_FOUND = "user-questionnaire/not-found",
	QUESTIONNAIRE_NOT_ANSWERED = "user-questionnaire/not-answered",

	/** Goal Validation ERROR */
	SETUP_GOAL_IN_USE = "setup-goal/in-use",

	/** Custom Recipients error */
	CUSTOM_RECIPIENTS_IS_USED = "custom-recipient/in-use",

	/** Generic Validation error */
	VALIDATION_ERROR = "validation-error",

	REAUTHENTICATE = "Please re-authenticate.",

	SOMETHING_WENT_WRONG = "something-went-wrong",

	/** Chat session error codes */
	SESSION_NOT_FOUND = "chat-session/not-found",
	MISSING_REQUIRED_FIELDS = "chat-session/missing-required-fields",

	MINIGAME_IN_USE = "minigame/in-use",

	STORY_INCOMPLETE = "story/incomplete",

	/** Create Employee ERROR */
	CREATE_EMPLOYEE_NEEDS_MANAGER = "create-employee/needs-manager",
}

export function getErrorMessage(code: string) {
	const codeMessages = {
		// Added this one to handle 401 Unauthorized status as it does not allow to have body. Find a good way to handle this situation
		[ErrorCode.REAUTHENTICATE]: "AUTH.error.invalid-credentials", //"Invalid username or password",

		[ErrorCode.AUTH_EMAIL_ALREADY_EXISTS]: "AUTH.error.email-already-exists", //"User already exists",
		[ErrorCode.AUTH_INVALID_CREDENTIALS]: "AUTH.error.invalid-credentials", //"Invalid username or password",
		[ErrorCode.AUTH_ROOM_NOT_AVAILABLE]: "AUTH.error.room-not-available", //"Room is not available",
		[ErrorCode.AUTH_ROOM_INACCESSIBLE]: "AUTH.error.room-inaccessible", //"You're not allowed to enter the room",
		[ErrorCode.AUTH_ROOM_NOT_EXIST]: "AUTH.error.room-not-exist", //"Room does not exist",
		[ErrorCode.AUTH_REGISTRATION_NOT_ALLOWED]: "AUTH.error.passwordless-registration-not-allowed",
		[ErrorCode.AUTH_EMAIL_NOT_PART_OF_ORG]: "AUTH.error.email-not-part-of-organization",
		[ErrorCode.AUTH_INVALID_OTP]: "AUTH.error.invalid-otp",
		[ErrorCode.AUTH_OTP_EXPIRED]: "AUTH.error.invalid-otp",
		[ErrorCode.AUTH_EMAIL_ALREADY_VERIFIED]: "AUTH.error.email-already-verified",
		[ErrorCode.AUTH_USER_NOT_FOUND]: "AUTH.error.user-not-found",
		[ErrorCode.AUTH_ONLY_SUPER_ADMIN_ALLOWED]: "AUTH.error.only-super-admin-allowed",
		[ErrorCode.AUTH_TOS_NOT_ACCEPTED]: "AUTH.error.tos-not-accepted",
		[ErrorCode.AUTH_DOMAIN_NOT_ALLOWED]: "AUTH.error.domain-not-allowed",

		[ErrorCode.FILE_TYPE_NOT_ALLOWED]: "FILE.error.type-not-allowed",
		[ErrorCode.FILE_SIZE_EXCEED]: "FILE.error.size-exceed",
		[ErrorCode.FILE_NOT_FOUND]: "FILE.error.not-found",
		[ErrorCode.FILE_INVALID_DATA]: "FILE.error.invalid-file-data",

		[ErrorCode.JOURNEY_IN_DRAFT]: "JOURNEY.error.in-draft",
		[ErrorCode.JOURNEY_IN_USE]: "JOURNEY.error.in-use",
		[ErrorCode.JOURNEY_NOT_FOUND]: "JOURNEY.error.not-found",
		[ErrorCode.JOURNEY_NOT_TYPE_UPLOAD_FILE]: "JOURNEY.error.not-upload-file",
		[ErrorCode.JOURNEY_NO_ACTIVITY_FOUND]: "JOURNEY.error.no-activity-found",
		[ErrorCode.JOURNEY_ROUTE_NOT_EXIST]: "JOURNEY.error.publish-route-not-exist",
		[ErrorCode.JOURNEY_USER_ALREADY_ASSIGNED]: "JOURNEY.error.user-already-assigned",
		[ErrorCode.JOURNEY_USER_FORBIDDEN_ASSIGN]: "JOURNEY.error.user-forbidden-assign",

		[ErrorCode.ORG_NOT_FOUND]: "ORG.error.not-found",
		[ErrorCode.ORG_PLAN_NOT_FOUND]: "ORG.error.plan-not-found",
		[ErrorCode.ORG_LOCATION_NOT_FOUND]: "ORG.error.location.not-found",
		[ErrorCode.ORG_DEPARTMENT_NOT_FOUND]: "ORG.error.department.not-found",
		[ErrorCode.ORG_DEPARTMENT_IN_USE]: "ORG.error.department.in-use",
		[ErrorCode.ORG_LOCATION_IN_USE]: "ORG.error.location.in-use",
		[ErrorCode.ORG_PROFILE_INVALID_CAREER_PAGE_URL]: "ORG.error.organization-profile.invalid-career-page-url",
		[ErrorCode.ORG_PROFILE_INVALID_WEBSITE_URL]: "ORG.error.organization-profile.invalid-website-url",
		[ErrorCode.ORG_PROFILE_INVALID_LINKEDIN_URL]: "ORG.error.organization-profile.invalid-linkedIn-url",

		[ErrorCode.ROOM_NOT_FOUND]: "ROOM.error.not-found",
		[ErrorCode.ROOM_TEMPLATE_NOT_FOUND]: "ROOM.error.template-not-found",
		[ErrorCode.ROOM_TEMPLATE_USED_IN_JOURNEY]: "ROOM.error.template-used-in-journey",

		[ErrorCode.STRIPE_ASSIGN_JOURNEY_CREDIT_LIMIT]: "STRIPE.error.assign-journey-credit-limit",
		[ErrorCode.STRIPE_JOURNEY_BUILDER_CREDIT_LIMIT]: "STRIPE.error.journey-builder-credit-limit",
		[ErrorCode.STRIPE_STORY_CREDIT_LIMIT]: "STRIPE.error.story-credit-limit",
		[ErrorCode.STRIPE_UNPAID_SUBSCRIPTION]: "STRIPE.error.unpaid-subscription",
		[ErrorCode.STRIPE_NOT_ENABLED]: "STRIPE.error.not-enabled",
		[ErrorCode.STRIPE_CANCELLED_AT_PERIOD_END]: "STRIPE.error.cancelled-at-period-end",

		[ErrorCode.DAILY_MISSION_NOT_FOUND]: "DASHBOARD.employee-journey.daily-mission.error.not-found",
		[ErrorCode.DAILY_MISSION_WRONG_PIN]: "DASHBOARD.employee-journey.daily-mission.error.pin",
		[ErrorCode.DAILY_MISSION_ORDER_ALREADY_EXISTS]: "DASHBOARD.employee-journey.daily-mission.error.order",

		[ErrorCode.USER_GOAL_NOT_FOUND]: "DASHBOARD.employee-journey.activity_type.goal_setting.user-goal-not-found",
		[ErrorCode.USER_GOAL_IS_COMPLETED]:
			"DASHBOARD.employee-journey.activity_type.goal_setting.user-goal-already-completed",

		[ErrorCode.SETUP_GOAL_IN_USE]:
			"DASHBOARD.employee-journey.activity_type.goal_setting.setup-goal-already-exists",

		[ErrorCode.QUESTIONNAIRE_NOT_FOUND]:
			"DASHBOARD.employee-journey.question-activity.error.questionnare-not-found",
		[ErrorCode.QUESTIONNAIRE_ORDER_ALREADY_EXISTS]:
			"DASHBOARD.employee-journey.question-activity.error.order-already-exists",
		[ErrorCode.QUESTION_NOT_FOUND]: "DASHBOARD.employee-journey.question-activity.error.question-not-found",
		[ErrorCode.USER_QUESTIONNAIRE_NOT_FOUND]:
			"DASHBOARD.employee-journey.question-activity.error.user-questionnare-not-found",
		[ErrorCode.QUESTIONNAIRE_NOT_ANSWERED]:
			"DASHBOARD.employee-journey.question-activity.error.questionnare-not-answered",
		[ErrorCode.TASK_NOT_FOUND]: "DASHBOARD.employee-journey.task-activity.error.task-not-found",
		[ErrorCode.USER_TASK_NOT_FOUND]: "USER-DASHBOARD.task.not-found",

		[ErrorCode.EMAIL_RATE_LIMIT_HOURLY]: "USER-DASHBOARD.notify-buddy.error.limit-hourly",
		[ErrorCode.EMAIL_RATE_LIMIT_FREQUENCY]: "USER-DASHBOARD.notify-buddy.error.limit-frequency",

		[ErrorCode.CUSTOM_RECIPIENTS_IS_USED]: "ORG-SETTINGS.custom-recipients.delete-recipient.error.in_use",

		[ErrorCode.VALIDATION_ERROR]: "VALIDATION.error",

		[ErrorCode.EXPIRED_RESET_PASSWORD]: "EXPIRED.reset-password",
		[ErrorCode.MINIGAME_IN_USE]: "DASHBOARD.mini-game.error.in-use",
		[ErrorCode.STORY_INCOMPLETE]: "ROOM-TEMPLATE.error.incomplete-story",
		[ErrorCode.CREATE_EMPLOYEE_NEEDS_MANAGER]: "ADD-EMPLOYEE.error.employee-needs-manager",
	};

	const i18Key = codeMessages[code] || "GLOBAL.something-went-wrong";
	return getIntl().formatMessage({ id: i18Key });
}
